import { useState, useEffect, useMemo } from 'react';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import { X, Filter, ChevronDown, ChevronUp, Plane, Bus, ArrowUpDown, User, Music, MapPin, Mic } from 'lucide-react';

export const FiltersPanel = ({ 
  packages, 
  operators,
  selectedOperators, 
  onOperatorChange,
  priceRange,
  onPriceChange,
  selectedTransport,
  onTransportChange,
  sortOrder,
  onSortChange,
  onReset,
  isConcerts = false,
  concertFilters = null,
  selectedGenre,
  onGenreChange,
  selectedArtist,
  onArtistChange,
  selectedCity,
  onCityChange
}) => {
  const [showMobile, setShowMobile] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    operators: true,
    price: true,
    transport: true,
    sort: true,
    genre: true,
    artist: true,
    city: true
  });

  // Helper function to parse price from string or number
  const parsePrice = (price) => {
    if (typeof price === 'number') return price;
    if (typeof price === 'string') {
      // Extract number from string like "569 €" or "1.234 €"
      const match = price.replace(/\./g, '').match(/[\d]+/);
      return match ? parseInt(match[0]) : 0;
    }
    return 0;
  };

  // Calculate min/max prices from packages
  const { minPrice, maxPrice } = useMemo(() => {
    if (!packages || packages.length === 0) return { minPrice: 0, maxPrice: 5000 };
    const prices = packages.map(p => parsePrice(p.price)).filter(p => p > 0);
    if (prices.length === 0) return { minPrice: 0, maxPrice: 5000 };
    return {
      minPrice: Math.floor(Math.min(...prices)),
      maxPrice: Math.ceil(Math.max(...prices))
    };
  }, [packages]);

  // Calculate transport counts
  const transportCounts = useMemo(() => {
    if (!packages) return { avion: 0, autocar: 0, Individual: 0 };
    return packages.reduce((acc, pkg) => {
      const type = pkg.transport_type || 'avion';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, { avion: 0, autocar: 0, Individual: 0 });
  }, [packages]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleOperatorToggle = (operatorName) => {
    if (selectedOperators.includes(operatorName)) {
      onOperatorChange(selectedOperators.filter(o => o !== operatorName));
    } else {
      onOperatorChange([...selectedOperators, operatorName]);
    }
  };

  const handleTransportToggle = (type) => {
    if (selectedTransport.includes(type)) {
      onTransportChange(selectedTransport.filter(t => t !== type));
    } else {
      onTransportChange([...selectedTransport, type]);
    }
  };

  const hasActiveFilters = selectedOperators.length > 0 || 
    selectedTransport.length > 0 || 
    sortOrder !== 'none' ||
    (selectedGenre && selectedGenre !== '') ||
    (selectedArtist && selectedArtist !== '') ||
    (selectedCity && selectedCity !== '');

  const FilterContent = () => (
    <div className="space-y-4">
      {/* Sortare */}
      <div className="border-b border-white/10 pb-4">
        <button 
          onClick={() => toggleSection('sort')}
          className="flex items-center justify-between w-full mb-3"
        >
          <h4 className="font-heading font-semibold text-white flex items-center gap-2">
            <ArrowUpDown className="w-4 h-4 text-gold" />
            Sortare preț
          </h4>
          {expandedSections.sort ? <ChevronUp className="w-4 h-4 text-white/60" /> : <ChevronDown className="w-4 h-4 text-white/60" />}
        </button>
        
        {expandedSections.sort && (
          <div className="space-y-2">
            <label className="flex items-center gap-3 cursor-pointer group">
              <Checkbox
                checked={sortOrder === 'asc'}
                onCheckedChange={() => onSortChange(sortOrder === 'asc' ? 'none' : 'asc')}
                className="border-white/30 data-[state=checked]:bg-gold data-[state=checked]:border-gold"
              />
              <span className="text-white/80 group-hover:text-white transition-colors text-sm">
                Preț crescător
              </span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer group">
              <Checkbox
                checked={sortOrder === 'desc'}
                onCheckedChange={() => onSortChange(sortOrder === 'desc' ? 'none' : 'desc')}
                className="border-white/30 data-[state=checked]:bg-gold data-[state=checked]:border-gold"
              />
              <span className="text-white/80 group-hover:text-white transition-colors text-sm">
                Preț descrescător
              </span>
            </label>
          </div>
        )}
      </div>

      {/* Concert Filters - Genre */}
      {isConcerts && concertFilters?.genres?.length > 0 && (
        <div className="border-b border-white/10 pb-4">
          <button onClick={() => toggleSection('genre')} className="flex items-center justify-between w-full mb-3">
            <h4 className="font-heading font-semibold text-white flex items-center gap-2">
              <Music className="w-4 h-4 text-purple-400" />
              Gen Muzical
            </h4>
            {expandedSections.genre ? <ChevronUp className="w-4 h-4 text-white/60" /> : <ChevronDown className="w-4 h-4 text-white/60" />}
          </button>
          {expandedSections.genre && (
            <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
              {concertFilters.genres.map(genre => (
                <label key={genre} className="flex items-center gap-3 cursor-pointer group" data-testid={`genre-filter-${genre}`}>
                  <Checkbox
                    checked={selectedGenre === genre}
                    onCheckedChange={() => onGenreChange(selectedGenre === genre ? '' : genre)}
                    className="border-white/30 data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple-500"
                  />
                  <span className="text-white/80 group-hover:text-white transition-colors text-sm">{genre}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Concert Filters - Artist */}
      {isConcerts && concertFilters?.artists?.length > 0 && (
        <div className="border-b border-white/10 pb-4">
          <button onClick={() => toggleSection('artist')} className="flex items-center justify-between w-full mb-3">
            <h4 className="font-heading font-semibold text-white flex items-center gap-2">
              <Mic className="w-4 h-4 text-purple-400" />
              Artist
            </h4>
            {expandedSections.artist ? <ChevronUp className="w-4 h-4 text-white/60" /> : <ChevronDown className="w-4 h-4 text-white/60" />}
          </button>
          {expandedSections.artist && (
            <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
              {concertFilters.artists.map(artist => (
                <label key={artist} className="flex items-center gap-3 cursor-pointer group" data-testid={`artist-filter-${artist}`}>
                  <Checkbox
                    checked={selectedArtist === artist}
                    onCheckedChange={() => onArtistChange(selectedArtist === artist ? '' : artist)}
                    className="border-white/30 data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple-500"
                  />
                  <span className="text-white/80 group-hover:text-white transition-colors text-sm">{artist}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Concert Filters - City */}
      {isConcerts && concertFilters?.cities?.length > 0 && (
        <div className="border-b border-white/10 pb-4">
          <button onClick={() => toggleSection('city')} className="flex items-center justify-between w-full mb-3">
            <h4 className="font-heading font-semibold text-white flex items-center gap-2">
              <MapPin className="w-4 h-4 text-purple-400" />
              Oraș
            </h4>
            {expandedSections.city ? <ChevronUp className="w-4 h-4 text-white/60" /> : <ChevronDown className="w-4 h-4 text-white/60" />}
          </button>
          {expandedSections.city && (
            <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
              {concertFilters.cities.map(city => (
                <label key={city} className="flex items-center gap-3 cursor-pointer group" data-testid={`city-filter-${city}`}>
                  <Checkbox
                    checked={selectedCity === city}
                    onCheckedChange={() => onCityChange(selectedCity === city ? '' : city)}
                    className="border-white/30 data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple-500"
                  />
                  <span className="text-white/80 group-hover:text-white transition-colors text-sm">{city}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Transport - hidden for concerts */}
      {!isConcerts && (
      <div className="border-b border-white/10 pb-4">
        <button 
          onClick={() => toggleSection('transport')}
          className="flex items-center justify-between w-full mb-3"
        >
          <h4 className="font-heading font-semibold text-white">Transport</h4>
          {expandedSections.transport ? <ChevronUp className="w-4 h-4 text-white/60" /> : <ChevronDown className="w-4 h-4 text-white/60" />}
        </button>
        
        {expandedSections.transport && (
          <div className="space-y-2">
            <label className="flex items-center gap-3 cursor-pointer group" data-testid="transport-avion">
              <Checkbox
                checked={selectedTransport.includes('avion')}
                onCheckedChange={() => handleTransportToggle('avion')}
                className="border-white/30 data-[state=checked]:bg-gold data-[state=checked]:border-gold"
              />
              <Plane className="w-4 h-4 text-white/60" />
              <span className="text-white/80 group-hover:text-white transition-colors text-sm flex-1">
                Avion
              </span>
              <span className="text-white/40 text-xs">({transportCounts.avion})</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer group" data-testid="transport-autocar">
              <Checkbox
                checked={selectedTransport.includes('autocar')}
                onCheckedChange={() => handleTransportToggle('autocar')}
                className="border-white/30 data-[state=checked]:bg-gold data-[state=checked]:border-gold"
              />
              <Bus className="w-4 h-4 text-white/60" />
              <span className="text-white/80 group-hover:text-white transition-colors text-sm flex-1">
                Autocar
              </span>
              <span className="text-white/40 text-xs">({transportCounts.autocar})</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer group" data-testid="transport-individual">
              <Checkbox
                checked={selectedTransport.includes('Individual')}
                onCheckedChange={() => handleTransportToggle('Individual')}
                className="border-white/30 data-[state=checked]:bg-gold data-[state=checked]:border-gold"
              />
              <User className="w-4 h-4 text-white/60" />
              <span className="text-white/80 group-hover:text-white transition-colors text-sm flex-1">
                Individual
              </span>
              <span className="text-white/40 text-xs">({transportCounts.Individual})</span>
            </label>
          </div>
        )}
      </div>
      )}

      {/* Operatori */}
      {operators.length > 0 && (
        <div>
          <button 
            onClick={() => toggleSection('operators')}
            className="flex items-center justify-between w-full mb-3"
          >
            <h4 className="font-heading font-semibold text-white">Comercianți</h4>
            {expandedSections.operators ? <ChevronUp className="w-4 h-4 text-white/60" /> : <ChevronDown className="w-4 h-4 text-white/60" />}
          </button>
          
          {expandedSections.operators && (
            <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
              {operators.map((op) => (
                <label 
                  key={op.name} 
                  className="flex items-center gap-3 cursor-pointer group"
                  data-testid={`operator-filter-${op.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Checkbox
                    checked={selectedOperators.includes(op.name)}
                    onCheckedChange={() => handleOperatorToggle(op.name)}
                    className="border-white/30 data-[state=checked]:bg-gold data-[state=checked]:border-gold"
                  />
                  <span className="text-white/80 group-hover:text-white transition-colors text-sm flex-1 truncate">
                    {op.name}
                  </span>
                  <span className="text-white/40 text-xs">({op.count})</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Reset button */}
      {hasActiveFilters && (
        <Button
          onClick={onReset}
          variant="outline"
          className="w-full mt-4 border-gold/50 text-gold hover:bg-gold/10"
          data-testid="reset-all-filters"
        >
          <X className="w-4 h-4 mr-2" />
          Resetează filtrele
        </Button>
      )}
    </div>
  );

  return (
    <>
      {/* Desktop Filter */}
      <div className="hidden md:block glass rounded-xl p-5" data-testid="filters-panel-desktop">
        <FilterContent />
      </div>

      {/* Mobile Filter Button */}
      <div className="md:hidden">
        <Button
          onClick={() => setShowMobile(true)}
          className="btn-outline flex items-center gap-2"
          data-testid="mobile-filter-btn"
        >
          <Filter className="w-4 h-4" />
          Filtre
          {hasActiveFilters && (
            <span className="bg-gold text-navy text-xs px-2 py-0.5 rounded-full">
              Activ
            </span>
          )}
        </Button>
      </div>

      {/* Mobile Filter Drawer */}
      {showMobile && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div 
            className="absolute inset-0 bg-navy/80 backdrop-blur-sm"
            onClick={() => setShowMobile(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 glass-heavy rounded-t-3xl p-6 animate-slide-up max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading font-semibold text-lg text-white">Filtre</h3>
              <button 
                onClick={() => setShowMobile(false)}
                className="text-white/60 hover:text-white"
                data-testid="close-mobile-filter"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <FilterContent />
            <Button
              onClick={() => setShowMobile(false)}
              className="w-full btn-gold mt-6"
              data-testid="apply-filters-btn"
            >
              Aplică filtre
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
